"use client";

import { c as _c } from "react/compiler-runtime";
import type { Editor } from "@tiptap/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import dynamic from "next/dynamic";
import NoteAttachmentSection from "@/app/[tenant]/(console)/scribe/playground/[id]/note-attachment-section";
import NoteDocumentsSection from "@/app/[tenant]/(console)/scribe/playground/[id]/note-documents-section";
import PatientReportedMedicationInput from "@/components/patient-reported-medication-input";
import { MeasurementSourceBadge } from "@/components/patient/measurements/measurement-source-badge";
import { ProviderSignatureInput } from "@/components/provider/provider-signature-input";
import { Icd10CodesInput } from "@/components/scribe/icd10-codes-answer";
import TiptapEditor from "@/components/tiptap/editor";
import { Button, Input, Label } from "@/components/ui";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerWithYearDropdown } from "@/components/ui/date-picker";
import HeightInput from "@/components/ui/height-input";
import { MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectPills, MultiSelectTrigger } from "@/components/ui/multi-select";
import MultiTagInput from "@/components/ui/multi-tag";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import DeselectableSelectItem from "@/components/ui/select/deselectable-select-item";
import Switch from "@/components/ui/switch";
import { TimeInput } from "@/components/ui/time-input";
import { useOptionalScribeSuggestionsActions } from "@/contexts/scribe-suggestions-provider";
import { useUser } from "@/contexts/user-provider";
import { clinicClockFor, pickedDay } from "@/lib/clinic-clock";
import { fromDateOnlyString, joinDateAnswer, splitDateAnswer } from "@/lib/format-data";
import { canClearAnswer, nextToggleValue } from "@/lib/forms/deselect";
import { formatOptionValue, optionDisplay, optionLabelsByValue } from "@/lib/forms/option-display";
import { isIcd10CodesQuestion, readIcd10Answer, toIcd10AnswerData } from "@/lib/icd10-codes-field";
import { isPatientReportedMedicationQuestion, serializePatientReportedMedications, toPatientReportedMedicationEntries } from "@/lib/patient-reported-medications";
import { cx } from "@/lib/tailwind-utils";
import { useStoreTimezone } from "@/lib/timezone";
import { Date as DateQuestion, DentalChartAnswer, DentalChart as DentalChartConfig, Disclaimer, MultiSelect as MultiSelectType, Number, OcclusionExamAnswer, OcclusionExam as OcclusionExamConfig, PerioChartAnswer, PerioChart as PerioChartConfig, ProviderSignature as ProviderSignatureConfig, QuestionData, QuestionType, Rebooking, Signature as SignatureConfig, SingleSelect, Statement, SuppliesUsed, Tag, Text } from "@/types/forms/question";
import { NoteAnswerDetail, NoteQuestionDetail } from "@/types/notes";
import { MedicationAnswerEditor } from "./medication-answer-editor";
import { RebookingAnswerEditor } from "./rebooking-answer-editor";
import { SoldItemsAnswerEditor } from "./sold-items-answer-editor";
import { SuppliesUsedAnswerEditor } from "./supplies-used-answer-editor";
import { TableAnswerEditor } from "./table-answer-editor";
import { WeightAnswerEditor } from "./weight-answer-editor";
import { useSuggestions } from "@/api/query/provider/suggestions";
const DentalChartLoading = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-32 animate-pulse rounded-lg bg-background-secondary/30" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const DentalChartComponent = dynamic(() => import("@/components/dental/dental-chart"), {
  ssr: false,
  loading: DentalChartLoading
});
const OcclusionExamComponent = dynamic(() => import("@/components/dental/occlusion-exam"), {
  ssr: false,
  loading: DentalChartLoading
});
const PerioChartComponent = dynamic(() => import("@/components/dental/perio-chart"), {
  ssr: false,
  loading: DentalChartLoading
});

/**
 * How one option of a select question reads while a provider fills the note.
 *
 * A question bound to a measurement field carries the clinic's wording for each value,
 * and the note has to read the way the form does — a bare `0` next to `4` says nothing
 * about which end of the scale is which. Without labels this is the plain option, which
 * is every question not bound to a field.
 */
const selectOptionDisplay = (data: {
  options?: string[];
  optionLabels?: string[];
}) => {
  const labelsByValue = optionLabelsByValue(data.options, data.optionLabels);
  return (option: string) => optionDisplay(formatOptionValue(option), labelsByValue?.[option]);
};
interface NoteAnswerEditorProps {
  answer: NoteAnswerDetail;
  question: NoteQuestionDetail;
  onChange: (answerId: string, data: Record<string, any>, noteAttachmentId?: string) => void;
  disabled?: boolean;
  noteId?: string;
  patientId?: string;
  fullHeight?: boolean;
  showPatientReportedPrefill?: boolean;
  /** Optional patient + visit context, used by DENTAL_CHART for the header band. */
  patient?: {
    name?: string;
    age?: number | string;
    avatarUrl?: string;
    externalId?: string | number;
  };
  visitDate?: string;
}

/**
 * The date half of a DATE answer, plus the time half when the question asks for
 * one. A time typed before a date has nowhere to be stored — the answer is keyed
 * off the date — so it is held here until a date arrives rather than locking the
 * field.
 */
const DateAnswerField: React.FC<{
  name: string;
  value: string | null;
  includeTime: boolean;
  showTodayButton: boolean;
  disabled: boolean;
  onChange: (value: string | null) => void;
}> = t0 => {
  const $ = _c(41);
  const {
    name,
    value,
    includeTime,
    showTodayButton,
    disabled,
    onChange
  } = t0;
  const timeZone = useStoreTimezone();
  const {
    date: datePart,
    time: timePart
  } = splitDateAnswer(value, timeZone);
  const [pendingTime, setPendingTime] = useState("");
  const time = timePart || pendingTime;
  const dateValue = datePart ? (() => {
    const parsed = fromDateOnlyString(datePart);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  })() : undefined;
  let t1;
  if ($[0] !== datePart || $[1] !== time) {
    t1 = time ? new Date(`${datePart || "2000-01-01"}T${time}`) : new Date(NaN);
    $[0] = datePart;
    $[1] = time;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const timeValue = t1;
  let t2;
  if ($[3] !== name) {
    t2 = <Label className="text-body-strong">{name}</Label>;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onChange || $[6] !== setPendingTime || $[7] !== time || $[8] !== timeZone) {
    t3 = date => {
      onChange(date ? joinDateAnswer(pickedDay(date), time, timeZone) : null);
      setPendingTime("");
    };
    $[5] = onChange;
    $[6] = setPendingTime;
    $[7] = time;
    $[8] = timeZone;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== dateValue || $[11] !== t3) {
    t4 = <div className="w-52 shrink-0"><DatePickerWithYearDropdown value={dateValue} onChange={t3} className="w-full" /></div>;
    $[10] = dateValue;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== datePart || $[14] !== disabled || $[15] !== includeTime || $[16] !== name || $[17] !== onChange || $[18] !== setPendingTime || $[19] !== timeValue || $[20] !== timeZone) {
    t5 = includeTime && <div className="w-40 shrink-0"><TimeInput date={timeValue} ariaLabel={`${name} time`} placeholder="hh:mm AM/PM" disabled={disabled} className="w-full" onClear={() => {
        setPendingTime("");
        if (datePart) {
          onChange(joinDateAnswer(datePart, "", timeZone));
        }
      }} onChange={picked => {
        const hhmm = `${String(picked.getHours()).padStart(2, "0")}:${String(picked.getMinutes()).padStart(2, "0")}`;
        if (datePart) {
          onChange(joinDateAnswer(datePart, hhmm, timeZone));
        } else {
          setPendingTime(hhmm);
        }
      }} /></div>;
    $[13] = datePart;
    $[14] = disabled;
    $[15] = includeTime;
    $[16] = name;
    $[17] = onChange;
    $[18] = setPendingTime;
    $[19] = timeValue;
    $[20] = timeZone;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== disabled || $[23] !== onChange || $[24] !== setPendingTime || $[25] !== showTodayButton || $[26] !== time || $[27] !== timeZone) {
    t6 = showTodayButton && <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => {
      onChange(joinDateAnswer(clinicClockFor(timeZone).today(), time, timeZone));
      setPendingTime("");
    }}>Today</Button>;
    $[22] = disabled;
    $[23] = onChange;
    $[24] = setPendingTime;
    $[25] = showTodayButton;
    $[26] = time;
    $[27] = timeZone;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t7 = <div className="flex flex-wrap items-center gap-2">{t4}{t5}{t6}</div>;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== datePart || $[34] !== includeTime || $[35] !== pendingTime) {
    t8 = includeTime && !datePart && pendingTime && <p className="text-caption text-foreground-muted">Pick a date to save this time.</p>;
    $[33] = datePart;
    $[34] = includeTime;
    $[35] = pendingTime;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t2 || $[38] !== t7 || $[39] !== t8) {
    t9 = <>{t2}{t7}{t8}</>;
    $[37] = t2;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
};
export const NoteAnswerEditor: React.FC<NoteAnswerEditorProps> = t0 => {
  const $ = _c(466);
  const {
    answer,
    question,
    onChange,
    disabled: t1,
    noteId,
    patientId,
    fullHeight: t2,
    showPatientReportedPrefill: t3,
    patient,
    visitDate
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const fullHeight = t2 === undefined ? false : t2;
  const showPatientReportedPrefill = t3 === undefined ? false : t3;
  const questionData = question.data as QuestionData;
  const {
    currentUser
  } = useUser();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["PERSONAL", "PATIENT", "SAVED", "TENANT", "NOTE_TEMPLATE"];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const t5 = currentUser?.id;
  let t6;
  if ($[1] !== question.templateId || $[2] !== t5) {
    t6 = {
      providerId: t5,
      noteTemplateId: question.templateId
    };
    $[1] = question.templateId;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const {
    data: t7
  } = useSuggestions(t4, patientId, undefined, t6);
  let t8;
  if ($[4] !== t7) {
    t8 = t7 === undefined ? [] : t7;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const allSuggestions = t8;
  let t9;
  if ($[6] !== allSuggestions || $[7] !== question.id) {
    let t10;
    if ($[9] !== question.id) {
      t10 = s => s.type !== "SAVED" || !s.noteQuestionIds || s.noteQuestionIds.length === 0 || s.noteQuestionIds.includes(question.id);
      $[9] = question.id;
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    t9 = allSuggestions.filter(t10);
    $[6] = allSuggestions;
    $[7] = question.id;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const suggestions = t9;
  const scribeSuggestionsActions = useOptionalScribeSuggestionsActions();
  const [editor, setEditor] = React.useState(null);
  let t10;
  let t11;
  if ($[11] !== editor || $[12] !== question.id || $[13] !== scribeSuggestionsActions) {
    t10 = () => {
      if (!scribeSuggestionsActions || !editor) {
        return;
      }
      return scribeSuggestionsActions.registerEditor(question.id, editor);
    };
    t11 = [scribeSuggestionsActions, editor, question.id];
    $[11] = editor;
    $[12] = question.id;
    $[13] = scribeSuggestionsActions;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  if ($[16] !== question.id || $[17] !== scribeSuggestionsActions) {
    t12 = () => {
      scribeSuggestionsActions?.setFocusedQuestionId(question.id);
    };
    $[16] = question.id;
    $[17] = scribeSuggestionsActions;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const handleEditorFocus = t12;
  let t13;
  if ($[19] !== answer.id || $[20] !== onChange) {
    t13 = (newData, noteAttachmentId) => {
      onChange(answer.id, newData, noteAttachmentId);
    };
    $[19] = answer.id;
    $[20] = onChange;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const handleChange = t13;
  if (question.questionType === QuestionType.MEDICATIONS) {
    let t14;
    if ($[22] !== answer || $[23] !== disabled || $[24] !== noteId || $[25] !== onChange || $[26] !== patientId) {
      t14 = <MedicationAnswerEditor answer={answer} onChange={onChange} disabled={disabled} noteId={noteId} patientId={patientId} />;
      $[22] = answer;
      $[23] = disabled;
      $[24] = noteId;
      $[25] = onChange;
      $[26] = patientId;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    return t14;
  }
  if (question.questionType === QuestionType.SOLD_ITEMS) {
    let t14;
    if ($[28] !== onChange) {
      t14 = (answerId, data) => onChange(answerId, data);
      $[28] = onChange;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== answer || $[31] !== disabled || $[32] !== questionData.name || $[33] !== t14) {
      t15 = <SoldItemsAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} />;
      $[30] = answer;
      $[31] = disabled;
      $[32] = questionData.name;
      $[33] = t14;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    return t15;
  }
  if (question.questionType === QuestionType.SUPPLIES_USED) {
    let t14;
    if ($[35] !== onChange) {
      t14 = (answerId_0, data_0) => onChange(answerId_0, data_0);
      $[35] = onChange;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    const t15 = question.data as SuppliesUsed;
    let t16;
    if ($[37] !== answer || $[38] !== disabled || $[39] !== noteId || $[40] !== questionData.name || $[41] !== t14 || $[42] !== t15) {
      t16 = <SuppliesUsedAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} questionData={t15} noteId={noteId} />;
      $[37] = answer;
      $[38] = disabled;
      $[39] = noteId;
      $[40] = questionData.name;
      $[41] = t14;
      $[42] = t15;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    return t16;
  }
  if (question.questionType === QuestionType.REBOOKING) {
    let t14;
    if ($[44] !== onChange) {
      t14 = (answerId_1, data_1) => onChange(answerId_1, data_1);
      $[44] = onChange;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    const t15 = question.data as Rebooking;
    let t16;
    if ($[46] !== answer || $[47] !== disabled || $[48] !== noteId || $[49] !== questionData.name || $[50] !== t14 || $[51] !== t15) {
      t16 = <RebookingAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} questionData={t15} noteId={noteId} />;
      $[46] = answer;
      $[47] = disabled;
      $[48] = noteId;
      $[49] = questionData.name;
      $[50] = t14;
      $[51] = t15;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    return t16;
  }
  if (question.questionType === QuestionType.TABLE) {
    let t14;
    if ($[53] !== onChange) {
      t14 = (answerId_2, data_2) => onChange(answerId_2, data_2);
      $[53] = onChange;
      $[54] = t14;
    } else {
      t14 = $[54];
    }
    let t15;
    if ($[55] !== answer || $[56] !== disabled || $[57] !== question || $[58] !== questionData.name || $[59] !== t14) {
      t15 = <TableAnswerEditor answer={answer} question={question} onChange={t14} disabled={disabled} title={questionData.name} />;
      $[55] = answer;
      $[56] = disabled;
      $[57] = question;
      $[58] = questionData.name;
      $[59] = t14;
      $[60] = t15;
    } else {
      t15 = $[60];
    }
    return t15;
  }
  if (isIcd10CodesQuestion(question)) {
    let t14;
    if ($[61] !== questionData.name) {
      t14 = <Label className="text-body-strong">{questionData.name}</Label>;
      $[61] = questionData.name;
      $[62] = t14;
    } else {
      t14 = $[62];
    }
    let t15;
    if ($[63] !== answer.data) {
      t15 = readIcd10Answer(answer.data);
      $[63] = answer.data;
      $[64] = t15;
    } else {
      t15 = $[64];
    }
    let t16;
    if ($[65] !== handleChange) {
      t16 = next => handleChange(toIcd10AnswerData(next));
      $[65] = handleChange;
      $[66] = t16;
    } else {
      t16 = $[66];
    }
    let t17;
    if ($[67] !== disabled || $[68] !== t15 || $[69] !== t16) {
      t17 = <Icd10CodesInput value={t15} onChange={t16} disabled={disabled} />;
      $[67] = disabled;
      $[68] = t15;
      $[69] = t16;
      $[70] = t17;
    } else {
      t17 = $[70];
    }
    let t18;
    if ($[71] !== t14 || $[72] !== t17) {
      t18 = <div className="space-y-2">{t14}{t17}</div>;
      $[71] = t14;
      $[72] = t17;
      $[73] = t18;
    } else {
      t18 = $[73];
    }
    return t18;
  }
  if (isPatientReportedMedicationQuestion(question)) {
    const textData = questionData as Text;
    const t14 = answer.data.valueDetails ?? answer.data.values ?? answer.data.value;
    let t15;
    if ($[74] !== t14) {
      t15 = toPatientReportedMedicationEntries(t14);
      $[74] = t14;
      $[75] = t15;
    } else {
      t15 = $[75];
    }
    const currentMedications = t15;
    let t16;
    if ($[76] !== handleChange) {
      t16 = medications => {
        handleChange({
          value: serializePatientReportedMedications(medications),
          valueDetails: medications
        });
      };
      $[76] = handleChange;
      $[77] = t16;
    } else {
      t16 = $[77];
    }
    const updateMedications = t16;
    const t17 = fullHeight && "flex h-full flex-col";
    let t18;
    if ($[78] !== t17) {
      t18 = cx("space-y-2", t17);
      $[78] = t17;
      $[79] = t18;
    } else {
      t18 = $[79];
    }
    let t19;
    if ($[80] !== questionData.name) {
      t19 = <Label className="flex-shrink-0 text-body-strong">{questionData.name}</Label>;
      $[80] = questionData.name;
      $[81] = t19;
    } else {
      t19 = $[81];
    }
    const t20 = textData.placeholder || "Add medication";
    let t21;
    if ($[82] !== currentMedications || $[83] !== disabled || $[84] !== t20 || $[85] !== updateMedications) {
      t21 = <PatientReportedMedicationInput medications={currentMedications} onChange={updateMedications} disabled={disabled} placeholder={t20} />;
      $[82] = currentMedications;
      $[83] = disabled;
      $[84] = t20;
      $[85] = updateMedications;
      $[86] = t21;
    } else {
      t21 = $[86];
    }
    let t22;
    if ($[87] !== t18 || $[88] !== t19 || $[89] !== t21) {
      t22 = <div className={t18}>{t19}{t21}</div>;
      $[87] = t18;
      $[88] = t19;
      $[89] = t21;
      $[90] = t22;
    } else {
      t22 = $[90];
    }
    return t22;
  }
  let t14;
  bb0: switch (question.questionType) {
    case QuestionType.TEXT:
      {
        const textData_0 = questionData as Text;
        let t15;
        if ($[91] !== questionData.name) {
          t15 = <Label className="flex-shrink-0 text-body-strong">{questionData.name}</Label>;
          $[91] = questionData.name;
          $[92] = t15;
        } else {
          t15 = $[92];
        }
        const t16 = answer.data.value || "";
        let t17;
        if ($[93] !== handleChange) {
          t17 = (text_0, __1, completeText_0) => handleChange({
            value: completeText_0 || text_0 || null
          });
          $[93] = handleChange;
          $[94] = t17;
        } else {
          t17 = $[94];
        }
        const t18 = fullHeight ? "h-full" : "min-h-[40px]";
        const t19 = fullHeight ? "flex flex-1 flex-col [&>div]:flex-1 [&_.ProseMirror]:h-full" : undefined;
        const t20 = !disabled;
        let t21;
        if ($[95] !== handleEditorFocus || $[96] !== suggestions || $[97] !== t16 || $[98] !== t17 || $[99] !== t18 || $[100] !== t19 || $[101] !== t20 || $[102] !== textData_0.placeholder) {
          t21 = <TiptapEditor content={t16} onUpdate={t17} suggestions={suggestions} expandSuggestionsOnInsert={true} richText={true} posthogSurface="note_inline" placeholder={textData_0.placeholder} textArea={false} editorClassName={t18} parentClassName={t19} onEditorReady={setEditor} onFocus={handleEditorFocus} editable={t20} />;
          $[95] = handleEditorFocus;
          $[96] = suggestions;
          $[97] = t16;
          $[98] = t17;
          $[99] = t18;
          $[100] = t19;
          $[101] = t20;
          $[102] = textData_0.placeholder;
          $[103] = t21;
        } else {
          t21 = $[103];
        }
        let t22;
        if ($[104] !== t15 || $[105] !== t21) {
          t22 = <>{t15}{t21}</>;
          $[104] = t15;
          $[105] = t21;
          $[106] = t22;
        } else {
          t22 = $[106];
        }
        t14 = t22;
        break bb0;
      }
    case QuestionType.TEXT_AREA:
      {
        const textAreaData = questionData as Text;
        let t15;
        if ($[107] !== questionData.name) {
          t15 = <Label className="flex-shrink-0 text-body-strong">{questionData.name}</Label>;
          $[107] = questionData.name;
          $[108] = t15;
        } else {
          t15 = $[108];
        }
        const t16 = answer.data.value || "";
        let t17;
        if ($[109] !== handleChange) {
          t17 = (text, __0, completeText) => handleChange({
            value: completeText || text || null
          });
          $[109] = handleChange;
          $[110] = t17;
        } else {
          t17 = $[110];
        }
        const t18 = !fullHeight;
        const t19 = fullHeight ? "h-full" : "min-h-[100px]";
        const t20 = fullHeight ? "flex flex-1 flex-col [&>div]:flex-1 [&_.ProseMirror]:h-full" : undefined;
        const t21 = !disabled;
        let t22;
        if ($[111] !== handleEditorFocus || $[112] !== suggestions || $[113] !== t16 || $[114] !== t17 || $[115] !== t18 || $[116] !== t19 || $[117] !== t20 || $[118] !== t21 || $[119] !== textAreaData.placeholder) {
          t22 = <TiptapEditor content={t16} onUpdate={t17} suggestions={suggestions} expandSuggestionsOnInsert={true} richText={true} posthogSurface="note_inline" placeholder={textAreaData.placeholder} textArea={true} resizable={t18} editorClassName={t19} parentClassName={t20} onEditorReady={setEditor} onFocus={handleEditorFocus} editable={t21} />;
          $[111] = handleEditorFocus;
          $[112] = suggestions;
          $[113] = t16;
          $[114] = t17;
          $[115] = t18;
          $[116] = t19;
          $[117] = t20;
          $[118] = t21;
          $[119] = textAreaData.placeholder;
          $[120] = t22;
        } else {
          t22 = $[120];
        }
        let t23;
        if ($[121] !== t15 || $[122] !== t22) {
          t23 = <>{t15}{t22}</>;
          $[121] = t15;
          $[122] = t22;
          $[123] = t23;
        } else {
          t23 = $[123];
        }
        t14 = t23;
        break bb0;
      }
    case QuestionType.POUNDS_WEIGHT:
      {
        let t15;
        if ($[124] !== answer || $[125] !== disabled || $[126] !== noteId || $[127] !== onChange || $[128] !== patientId || $[129] !== question) {
          t15 = <WeightAnswerEditor answer={answer} question={question} onChange={onChange} disabled={disabled} noteId={noteId} patientId={patientId} />;
          $[124] = answer;
          $[125] = disabled;
          $[126] = noteId;
          $[127] = onChange;
          $[128] = patientId;
          $[129] = question;
          $[130] = t15;
        } else {
          t15 = $[130];
        }
        t14 = t15;
        break bb0;
      }
    case QuestionType.NUMBER:
      {
        let currentValue = answer.data.value;
        if (currentValue === undefined || currentValue === null) {
          currentValue = "";
        }
        let t15;
        if ($[131] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[131] = questionData.name;
          $[132] = t15;
        } else {
          t15 = $[132];
        }
        let t16;
        if ($[133] !== handleChange) {
          t16 = e => {
            const value_5 = e.target.value ? parseFloat(e.target.value) : null;
            handleChange({
              value: value_5
            });
          };
          $[133] = handleChange;
          $[134] = t16;
        } else {
          t16 = $[134];
        }
        const t17 = questionData as Number;
        let t18;
        if ($[135] !== currentValue || $[136] !== disabled || $[137] !== t16 || $[138] !== t17.placeholder) {
          t18 = <Input type="number" stripLeadingZeros={false} value={currentValue} onChange={t16} placeholder={t17.placeholder} disabled={disabled} />;
          $[135] = currentValue;
          $[136] = disabled;
          $[137] = t16;
          $[138] = t17.placeholder;
          $[139] = t18;
        } else {
          t18 = $[139];
        }
        let t19;
        if ($[140] !== t15 || $[141] !== t18) {
          t19 = <>{t15}{t18}</>;
          $[140] = t15;
          $[141] = t18;
          $[142] = t19;
        } else {
          t19 = $[142];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.FEET_INCHES_HEIGHT:
      {
        let t15;
        if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = {};
          $[143] = t15;
        } else {
          t15 = $[143];
        }
        let heightData = t15;
        if (answer.data.value && typeof answer.data.value === "object") {
          heightData = answer.data.value as {
            feet?: number;
            inches?: number;
          };
        } else {
          if ("feet" in answer.data || "inches" in answer.data) {
            heightData = answer.data as {
              feet?: number;
              inches?: number;
            };
          }
        }
        let t16;
        if ($[144] !== questionData.name) {
          t16 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[144] = questionData.name;
          $[145] = t16;
        } else {
          t16 = $[145];
        }
        let t17;
        if ($[146] !== heightData.feet) {
          t17 = heightData.feet?.toString() ?? "";
          $[146] = heightData.feet;
          $[147] = t17;
        } else {
          t17 = $[147];
        }
        let t18;
        if ($[148] !== heightData.inches) {
          t18 = heightData.inches?.toString() ?? "";
          $[148] = heightData.inches;
          $[149] = t18;
        } else {
          t18 = $[149];
        }
        let t19;
        if ($[150] !== t17 || $[151] !== t18) {
          t19 = {
            feet: t17,
            inches: t18
          };
          $[150] = t17;
          $[151] = t18;
          $[152] = t19;
        } else {
          t19 = $[152];
        }
        let t20;
        if ($[153] !== handleChange) {
          t20 = value_4 => handleChange({
            value: {
              feet: value_4.feet ? parseInt(value_4.feet, 10) : 0,
              inches: value_4.inches ? parseFloat(value_4.inches) : 0
            }
          });
          $[153] = handleChange;
          $[154] = t20;
        } else {
          t20 = $[154];
        }
        let t21;
        if ($[155] !== disabled || $[156] !== t19 || $[157] !== t20) {
          t21 = <HeightInput value={t19} onChange={t20} quarterInches={true} disabled={disabled} />;
          $[155] = disabled;
          $[156] = t19;
          $[157] = t20;
          $[158] = t21;
        } else {
          t21 = $[158];
        }
        let t22;
        if ($[159] !== t16 || $[160] !== t21) {
          t22 = <>{t16}{t21}</>;
          $[159] = t16;
          $[160] = t21;
          $[161] = t22;
        } else {
          t22 = $[161];
        }
        t14 = t22;
        break bb0;
      }
    case QuestionType.DATE:
      {
        const t15 = questionData.name;
        const t16 = answer.data.value;
        const t17 = Boolean((questionData as DateQuestion).includeTime);
        const t18 = Boolean((questionData as DateQuestion).showTodayButton);
        let t19;
        if ($[162] !== handleChange) {
          t19 = value_3 => handleChange({
            value: value_3
          });
          $[162] = handleChange;
          $[163] = t19;
        } else {
          t19 = $[163];
        }
        let t20;
        if ($[164] !== answer.data.value || $[165] !== disabled || $[166] !== questionData.name || $[167] !== t17 || $[168] !== t18 || $[169] !== t19) {
          t20 = <DateAnswerField name={t15} value={t16} includeTime={t17} showTodayButton={t18} disabled={disabled} onChange={t19} />;
          $[164] = answer.data.value;
          $[165] = disabled;
          $[166] = questionData.name;
          $[167] = t17;
          $[168] = t18;
          $[169] = t19;
          $[170] = t20;
        } else {
          t20 = $[170];
        }
        t14 = t20;
        break bb0;
      }
    case QuestionType.SINGLE_SELECT:
      {
        const singleSelectData_0 = questionData as SingleSelect;
        let T0;
        let T1;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let t20;
        if ($[171] !== answer.data.value || $[172] !== disabled || $[173] !== handleChange || $[174] !== question || $[175] !== questionData.name || $[176] !== singleSelectData_0) {
          const showOption_2 = selectOptionDisplay(singleSelectData_0);
          if ($[185] !== questionData.name) {
            t20 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[185] = questionData.name;
            $[186] = t20;
          } else {
            t20 = $[186];
          }
          T1 = Select;
          t17 = answer.data.value || "";
          if ($[187] !== handleChange) {
            t18 = value_2 => handleChange({
              value: value_2
            });
            $[187] = handleChange;
            $[188] = t18;
          } else {
            t18 = $[188];
          }
          if ($[189] === Symbol.for("react.memo_cache_sentinel")) {
            t19 = <SelectTrigger className="max-w-xs" wrapText={true}><SelectValue placeholder="Select an option" /></SelectTrigger>;
            $[189] = t19;
          } else {
            t19 = $[189];
          }
          T0 = SelectContent;
          t15 = true;
          t16 = singleSelectData_0.options?.map(option_3 => <DeselectableSelectItem key={option_3} value={option_3} disabled={disabled} onDeselect={canClearAnswer(question) && option_3 === answer.data.value ? () => handleChange({
            value: null
          }) : undefined}>{showOption_2(option_3)}</DeselectableSelectItem>);
          $[171] = answer.data.value;
          $[172] = disabled;
          $[173] = handleChange;
          $[174] = question;
          $[175] = questionData.name;
          $[176] = singleSelectData_0;
          $[177] = T0;
          $[178] = T1;
          $[179] = t15;
          $[180] = t16;
          $[181] = t17;
          $[182] = t18;
          $[183] = t19;
          $[184] = t20;
        } else {
          T0 = $[177];
          T1 = $[178];
          t15 = $[179];
          t16 = $[180];
          t17 = $[181];
          t18 = $[182];
          t19 = $[183];
          t20 = $[184];
        }
        let t21;
        if ($[190] !== T0 || $[191] !== t15 || $[192] !== t16) {
          t21 = <T0 wrapText={t15}>{t16}</T0>;
          $[190] = T0;
          $[191] = t15;
          $[192] = t16;
          $[193] = t21;
        } else {
          t21 = $[193];
        }
        let t22;
        if ($[194] !== T1 || $[195] !== t17 || $[196] !== t18 || $[197] !== t19 || $[198] !== t21) {
          t22 = <T1 value={t17} onValueChange={t18}>{t19}{t21}</T1>;
          $[194] = T1;
          $[195] = t17;
          $[196] = t18;
          $[197] = t19;
          $[198] = t21;
          $[199] = t22;
        } else {
          t22 = $[199];
        }
        let t23;
        if ($[200] !== t20 || $[201] !== t22) {
          t23 = <>{t20}{t22}</>;
          $[200] = t20;
          $[201] = t22;
          $[202] = t23;
        } else {
          t23 = $[202];
        }
        t14 = t23;
        break bb0;
      }
    case QuestionType.RADIAL_SINGLE_SELECT:
      {
        const singleSelectData = questionData as SingleSelect;
        let T0;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let t20;
        if ($[203] !== answer.data.value || $[204] !== answer.id || $[205] !== disabled || $[206] !== handleChange || $[207] !== question || $[208] !== questionData.name || $[209] !== singleSelectData) {
          const showOption_1 = selectOptionDisplay(singleSelectData);
          if ($[217] !== questionData.name) {
            t20 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[217] = questionData.name;
            $[218] = t20;
          } else {
            t20 = $[218];
          }
          T0 = RadioGroup;
          t15 = answer.data.value || "";
          if ($[219] !== handleChange) {
            t16 = value_1 => handleChange({
              value: value_1
            });
            $[219] = handleChange;
            $[220] = t16;
          } else {
            t16 = $[220];
          }
          t17 = disabled;
          t18 = "grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-x-6 gap-y-2";
          t19 = singleSelectData.options?.map(option_2 => <div key={option_2} className="flex items-center space-x-2"><RadioGroupItem value={option_2} id={`${answer.id}-${option_2}`} onClick={() => {
              if (canClearAnswer(question) && option_2 === answer.data.value) {
                handleChange({
                  value: null
                });
              }
            }} /><Label htmlFor={`${answer.id}-${option_2}`} className="min-w-0 cursor-pointer break-words text-body font-normal">{showOption_1(option_2)}</Label></div>);
          $[203] = answer.data.value;
          $[204] = answer.id;
          $[205] = disabled;
          $[206] = handleChange;
          $[207] = question;
          $[208] = questionData.name;
          $[209] = singleSelectData;
          $[210] = T0;
          $[211] = t15;
          $[212] = t16;
          $[213] = t17;
          $[214] = t18;
          $[215] = t19;
          $[216] = t20;
        } else {
          T0 = $[210];
          t15 = $[211];
          t16 = $[212];
          t17 = $[213];
          t18 = $[214];
          t19 = $[215];
          t20 = $[216];
        }
        let t21;
        if ($[221] !== T0 || $[222] !== t15 || $[223] !== t16 || $[224] !== t17 || $[225] !== t18 || $[226] !== t19) {
          t21 = <T0 value={t15} onValueChange={t16} disabled={t17} className={t18}>{t19}</T0>;
          $[221] = T0;
          $[222] = t15;
          $[223] = t16;
          $[224] = t17;
          $[225] = t18;
          $[226] = t19;
          $[227] = t21;
        } else {
          t21 = $[227];
        }
        let t22;
        if ($[228] !== t20 || $[229] !== t21) {
          t22 = <>{t20}{t21}</>;
          $[228] = t20;
          $[229] = t21;
          $[230] = t22;
        } else {
          t22 = $[230];
        }
        t14 = t22;
        break bb0;
      }
    case QuestionType.MULTI_SELECT:
      {
        const multiSelectData_0 = questionData as MultiSelectType;
        let T0;
        let T1;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let t20;
        let t21;
        let t22;
        let t23;
        if ($[231] !== answer.data.value || $[232] !== answer.data.values || $[233] !== disabled || $[234] !== handleChange || $[235] !== multiSelectData_0 || $[236] !== questionData.name) {
          const showOption_0 = selectOptionDisplay(multiSelectData_0);
          let t24;
          if ($[248] !== answer.data.value || $[249] !== answer.data.values) {
            t24 = Array.isArray(answer.data.values) ? answer.data.values : answer.data.value ? [answer.data.value] : [];
            $[248] = answer.data.value;
            $[249] = answer.data.values;
            $[250] = t24;
          } else {
            t24 = $[250];
          }
          const currentValues_0 = t24;
          if ($[251] !== questionData.name) {
            t23 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[251] = questionData.name;
            $[252] = t23;
          } else {
            t23 = $[252];
          }
          T1 = MultiSelect;
          t16 = multiSelectData_0.options?.map(option_0 => ({
            value: option_0,
            label: showOption_0(option_0)
          })) || [];
          t17 = currentValues_0;
          if ($[253] !== handleChange) {
            t18 = values => handleChange({
              values
            });
            $[253] = handleChange;
            $[254] = t18;
          } else {
            t18 = $[254];
          }
          t19 = "Select options...";
          t20 = disabled;
          t21 = true;
          if ($[255] === Symbol.for("react.memo_cache_sentinel")) {
            t22 = <MultiSelectTrigger className="max-w-xs" />;
            $[255] = t22;
          } else {
            t22 = $[255];
          }
          T0 = MultiSelectContent;
          t15 = multiSelectData_0.options?.map(option_1 => <MultiSelectItem key={option_1} value={option_1}>{showOption_0(option_1)}</MultiSelectItem>);
          $[231] = answer.data.value;
          $[232] = answer.data.values;
          $[233] = disabled;
          $[234] = handleChange;
          $[235] = multiSelectData_0;
          $[236] = questionData.name;
          $[237] = T0;
          $[238] = T1;
          $[239] = t15;
          $[240] = t16;
          $[241] = t17;
          $[242] = t18;
          $[243] = t19;
          $[244] = t20;
          $[245] = t21;
          $[246] = t22;
          $[247] = t23;
        } else {
          T0 = $[237];
          T1 = $[238];
          t15 = $[239];
          t16 = $[240];
          t17 = $[241];
          t18 = $[242];
          t19 = $[243];
          t20 = $[244];
          t21 = $[245];
          t22 = $[246];
          t23 = $[247];
        }
        let t24;
        if ($[256] !== T0 || $[257] !== t15) {
          t24 = <T0>{t15}</T0>;
          $[256] = T0;
          $[257] = t15;
          $[258] = t24;
        } else {
          t24 = $[258];
        }
        let t25;
        if ($[259] === Symbol.for("react.memo_cache_sentinel")) {
          t25 = <MultiSelectPills className="mt-2" />;
          $[259] = t25;
        } else {
          t25 = $[259];
        }
        let t26;
        if ($[260] !== T1 || $[261] !== t16 || $[262] !== t17 || $[263] !== t18 || $[264] !== t19 || $[265] !== t20 || $[266] !== t21 || $[267] !== t22 || $[268] !== t24) {
          t26 = <T1 options={t16} value={t17} onChange={t18} placeholder={t19} disabled={t20} showOffListValues={t21}>{t22}{t24}{t25}</T1>;
          $[260] = T1;
          $[261] = t16;
          $[262] = t17;
          $[263] = t18;
          $[264] = t19;
          $[265] = t20;
          $[266] = t21;
          $[267] = t22;
          $[268] = t24;
          $[269] = t26;
        } else {
          t26 = $[269];
        }
        let t27;
        if ($[270] !== t23 || $[271] !== t26) {
          t27 = <>{t23}{t26}</>;
          $[270] = t23;
          $[271] = t26;
          $[272] = t27;
        } else {
          t27 = $[272];
        }
        t14 = t27;
        break bb0;
      }
    case QuestionType.RADIAL_MULTI_SELECT:
      {
        const multiSelectData = questionData as MultiSelectType;
        let t15;
        let t16;
        let t17;
        if ($[273] !== answer.data.value || $[274] !== answer.data.values || $[275] !== answer.id || $[276] !== disabled || $[277] !== handleChange || $[278] !== multiSelectData || $[279] !== questionData.name) {
          const showOption = selectOptionDisplay(multiSelectData);
          const currentValues = Array.isArray(answer.data.values) ? answer.data.values : answer.data.value ? [answer.data.value] : [];
          if ($[283] !== questionData.name) {
            t17 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[283] = questionData.name;
            $[284] = t17;
          } else {
            t17 = $[284];
          }
          t15 = "grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-x-6 gap-y-2";
          t16 = multiSelectData.options?.map(option => {
            const isSelected = currentValues.includes(option);
            return <div key={option} className="flex items-center space-x-2"><Checkbox id={`${answer.id}-${option}`} checked={isSelected} onCheckedChange={checked_0 => {
                const newValues = checked_0 ? [...currentValues, option] : currentValues.filter(v_2 => v_2 !== option);
                handleChange({
                  values: newValues
                });
              }} disabled={disabled} /><Label htmlFor={`${answer.id}-${option}`} className="min-w-0 cursor-pointer break-words text-body font-normal">{showOption(option)}</Label></div>;
          });
          $[273] = answer.data.value;
          $[274] = answer.data.values;
          $[275] = answer.id;
          $[276] = disabled;
          $[277] = handleChange;
          $[278] = multiSelectData;
          $[279] = questionData.name;
          $[280] = t15;
          $[281] = t16;
          $[282] = t17;
        } else {
          t15 = $[280];
          t16 = $[281];
          t17 = $[282];
        }
        let t18;
        if ($[285] !== t15 || $[286] !== t16) {
          t18 = <div className={t15}>{t16}</div>;
          $[285] = t15;
          $[286] = t16;
          $[287] = t18;
        } else {
          t18 = $[287];
        }
        let t19;
        if ($[288] !== t17 || $[289] !== t18) {
          t19 = <>{t17}{t18}</>;
          $[288] = t17;
          $[289] = t18;
          $[290] = t19;
        } else {
          t19 = $[290];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.TAG:
      {
        const tagData = questionData as Tag;
        let t15;
        if ($[291] !== answer.data.value || $[292] !== answer.data.values) {
          t15 = Array.isArray(answer.data.values) ? answer.data.values : answer.data.value ? [answer.data.value] : [];
          $[291] = answer.data.value;
          $[292] = answer.data.values;
          $[293] = t15;
        } else {
          t15 = $[293];
        }
        const currentTags = t15;
        let t16;
        if ($[294] !== questionData.name) {
          t16 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[294] = questionData.name;
          $[295] = t16;
        } else {
          t16 = $[295];
        }
        let t17;
        let t18;
        if ($[296] !== currentTags || $[297] !== handleChange) {
          t17 = tag => handleChange({
            values: [...currentTags, tag]
          });
          t18 = index => handleChange({
            values: currentTags.filter((_, i) => i !== index)
          });
          $[296] = currentTags;
          $[297] = handleChange;
          $[298] = t17;
          $[299] = t18;
        } else {
          t17 = $[298];
          t18 = $[299];
        }
        const t19 = tagData.placeholder || "Add tag";
        let t20;
        if ($[300] !== currentTags || $[301] !== t17 || $[302] !== t18 || $[303] !== t19) {
          t20 = <MultiTagInput tags={currentTags} onAddTag={t17} onRemoveTag={t18} placeholder={t19} />;
          $[300] = currentTags;
          $[301] = t17;
          $[302] = t18;
          $[303] = t19;
          $[304] = t20;
        } else {
          t20 = $[304];
        }
        let t21;
        if ($[305] !== t16 || $[306] !== t20) {
          t21 = <>{t16}{t20}</>;
          $[305] = t16;
          $[306] = t20;
          $[307] = t21;
        } else {
          t21 = $[307];
        }
        t14 = t21;
        break bb0;
      }
    case QuestionType.TOGGLE:
      {
        const toggleValue = answer.data.value;
        let T0;
        let allowDeselect;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let unanswered;
        if ($[308] !== answer.id || $[309] !== question || $[310] !== toggleValue) {
          allowDeselect = canClearAnswer(question);
          unanswered = allowDeselect && toggleValue !== true && toggleValue !== false;
          t19 = "flex items-center space-x-2";
          T0 = Switch;
          t15 = `answer-${answer.id}`;
          t16 = toggleValue === true;
          t17 = unanswered;
          t18 = cx(unanswered && "bg-muted");
          $[308] = answer.id;
          $[309] = question;
          $[310] = toggleValue;
          $[311] = T0;
          $[312] = allowDeselect;
          $[313] = t15;
          $[314] = t16;
          $[315] = t17;
          $[316] = t18;
          $[317] = t19;
          $[318] = unanswered;
        } else {
          T0 = $[311];
          allowDeselect = $[312];
          t15 = $[313];
          t16 = $[314];
          t17 = $[315];
          t18 = $[316];
          t19 = $[317];
          unanswered = $[318];
        }
        let t20;
        if ($[319] !== allowDeselect || $[320] !== handleChange || $[321] !== toggleValue) {
          t20 = checked => handleChange({
            value: allowDeselect ? nextToggleValue(toggleValue) : checked === true
          });
          $[319] = allowDeselect;
          $[320] = handleChange;
          $[321] = toggleValue;
          $[322] = t20;
        } else {
          t20 = $[322];
        }
        let t21;
        if ($[323] !== T0 || $[324] !== disabled || $[325] !== t15 || $[326] !== t16 || $[327] !== t17 || $[328] !== t18 || $[329] !== t20) {
          t21 = <T0 id={t15} checked={t16} indeterminate={t17} className={t18} onCheckedChange={t20} disabled={disabled} />;
          $[323] = T0;
          $[324] = disabled;
          $[325] = t15;
          $[326] = t16;
          $[327] = t17;
          $[328] = t18;
          $[329] = t20;
          $[330] = t21;
        } else {
          t21 = $[330];
        }
        const t22 = `answer-${answer.id}`;
        let t23;
        if ($[331] !== questionData.name || $[332] !== t22) {
          t23 = <Label htmlFor={t22} className="cursor-pointer text-body-strong">{questionData.name}</Label>;
          $[331] = questionData.name;
          $[332] = t22;
          $[333] = t23;
        } else {
          t23 = $[333];
        }
        let t24;
        if ($[334] !== allowDeselect || $[335] !== toggleValue || $[336] !== unanswered) {
          t24 = allowDeselect && !unanswered && <span className="text-caption text-foreground-muted">{toggleValue ? "Yes" : "No"}</span>;
          $[334] = allowDeselect;
          $[335] = toggleValue;
          $[336] = unanswered;
          $[337] = t24;
        } else {
          t24 = $[337];
        }
        let t25;
        if ($[338] !== t19 || $[339] !== t21 || $[340] !== t23 || $[341] !== t24) {
          t25 = <div className={t19}>{t21}{t23}{t24}</div>;
          $[338] = t19;
          $[339] = t21;
          $[340] = t23;
          $[341] = t24;
          $[342] = t25;
        } else {
          t25 = $[342];
        }
        t14 = t25;
        break bb0;
      }
    case QuestionType.SCRIBE_ATTACHMENTS:
      {
        if (!noteId || !patientId) {
          let t15;
          if ($[343] !== questionData.name) {
            t15 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[343] = questionData.name;
            $[344] = t15;
          } else {
            t15 = $[344];
          }
          let t16;
          if ($[345] === Symbol.for("react.memo_cache_sentinel")) {
            t16 = <p className="text-caption text-foreground-secondary">Note must be saved before uploading files</p>;
            $[345] = t16;
          } else {
            t16 = $[345];
          }
          let t17;
          if ($[346] !== t15) {
            t17 = <>{t15}{t16}</>;
            $[346] = t15;
            $[347] = t17;
          } else {
            t17 = $[347];
          }
          t14 = t17;
          break bb0;
        }
        let t15;
        if ($[348] !== question.required) {
          t15 = question.required && <span className="ml-1 text-destructive">*</span>;
          $[348] = question.required;
          $[349] = t15;
        } else {
          t15 = $[349];
        }
        let t16;
        if ($[350] !== questionData.name || $[351] !== t15) {
          t16 = <Label className="text-body-strong">{questionData.name}{t15}</Label>;
          $[350] = questionData.name;
          $[351] = t15;
          $[352] = t16;
        } else {
          t16 = $[352];
        }
        let t17;
        if ($[353] !== disabled) {
          t17 = <NoteAttachmentSection disabled={disabled} />;
          $[353] = disabled;
          $[354] = t17;
        } else {
          t17 = $[354];
        }
        let t18;
        if ($[355] !== t16 || $[356] !== t17) {
          t18 = <>{t16}{t17}</>;
          $[355] = t16;
          $[356] = t17;
          $[357] = t18;
        } else {
          t18 = $[357];
        }
        t14 = t18;
        break bb0;
      }
    case QuestionType.DENTAL_CHART:
      {
        const cfg_1 = questionData as DentalChartConfig;
        let t15;
        if ($[358] !== answer.data?.value) {
          t15 = answer.data?.value as DentalChartAnswer ?? {
            teeth: {}
          };
          $[358] = answer.data?.value;
          $[359] = t15;
        } else {
          t15 = $[359];
        }
        const current_1 = t15;
        let t16;
        if ($[360] !== cfg_1.name) {
          t16 = <Label className="text-body-strong">{cfg_1.name}</Label>;
          $[360] = cfg_1.name;
          $[361] = t16;
        } else {
          t16 = $[361];
        }
        let t17;
        if ($[362] !== handleChange) {
          t17 = v_1 => handleChange({
            value: v_1
          });
          $[362] = handleChange;
          $[363] = t17;
        } else {
          t17 = $[363];
        }
        let t18;
        if ($[364] !== cfg_1 || $[365] !== current_1 || $[366] !== disabled || $[367] !== patient || $[368] !== t17 || $[369] !== visitDate) {
          t18 = <DentalChartComponent questionData={cfg_1} value={current_1} onChange={t17} disabled={disabled} patient={patient} visitDate={visitDate} />;
          $[364] = cfg_1;
          $[365] = current_1;
          $[366] = disabled;
          $[367] = patient;
          $[368] = t17;
          $[369] = visitDate;
          $[370] = t18;
        } else {
          t18 = $[370];
        }
        let t19;
        if ($[371] !== t16 || $[372] !== t18) {
          t19 = <>{t16}{t18}</>;
          $[371] = t16;
          $[372] = t18;
          $[373] = t19;
        } else {
          t19 = $[373];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.PERIO_CHART:
      {
        const cfg_0 = questionData as PerioChartConfig;
        let t15;
        if ($[374] !== answer.data?.value) {
          t15 = answer.data?.value as PerioChartAnswer ?? {
            teeth: {}
          };
          $[374] = answer.data?.value;
          $[375] = t15;
        } else {
          t15 = $[375];
        }
        const current_0 = t15;
        let t16;
        if ($[376] !== cfg_0.name) {
          t16 = <Label className="text-body-strong">{cfg_0.name}</Label>;
          $[376] = cfg_0.name;
          $[377] = t16;
        } else {
          t16 = $[377];
        }
        let t17;
        if ($[378] !== handleChange) {
          t17 = v_0 => handleChange({
            value: v_0
          });
          $[378] = handleChange;
          $[379] = t17;
        } else {
          t17 = $[379];
        }
        let t18;
        if ($[380] !== cfg_0 || $[381] !== current_0 || $[382] !== disabled || $[383] !== t17) {
          t18 = <PerioChartComponent questionData={cfg_0} value={current_0} onChange={t17} disabled={disabled} />;
          $[380] = cfg_0;
          $[381] = current_0;
          $[382] = disabled;
          $[383] = t17;
          $[384] = t18;
        } else {
          t18 = $[384];
        }
        let t19;
        if ($[385] !== t16 || $[386] !== t18) {
          t19 = <>{t16}{t18}</>;
          $[385] = t16;
          $[386] = t18;
          $[387] = t19;
        } else {
          t19 = $[387];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.OCCLUSION_EXAM:
      {
        const cfg = questionData as OcclusionExamConfig;
        let t15;
        if ($[388] !== answer.data?.value) {
          t15 = answer.data?.value as OcclusionExamAnswer ?? {};
          $[388] = answer.data?.value;
          $[389] = t15;
        } else {
          t15 = $[389];
        }
        const current = t15;
        let t16;
        if ($[390] !== cfg.name) {
          t16 = <Label className="text-body-strong">{cfg.name}</Label>;
          $[390] = cfg.name;
          $[391] = t16;
        } else {
          t16 = $[391];
        }
        let t17;
        if ($[392] !== handleChange) {
          t17 = v => handleChange({
            value: v
          });
          $[392] = handleChange;
          $[393] = t17;
        } else {
          t17 = $[393];
        }
        let t18;
        if ($[394] !== cfg || $[395] !== current || $[396] !== disabled || $[397] !== t17) {
          t18 = <OcclusionExamComponent questionData={cfg} value={current} onChange={t17} disabled={disabled} />;
          $[394] = cfg;
          $[395] = current;
          $[396] = disabled;
          $[397] = t17;
          $[398] = t18;
        } else {
          t18 = $[398];
        }
        let t19;
        if ($[399] !== t16 || $[400] !== t18) {
          t19 = <>{t16}{t18}</>;
          $[399] = t16;
          $[400] = t18;
          $[401] = t19;
        } else {
          t19 = $[401];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.FILE_UPLOAD:
      {
        if (!noteId) {
          let t15;
          if ($[402] !== questionData.name) {
            t15 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[402] = questionData.name;
            $[403] = t15;
          } else {
            t15 = $[403];
          }
          let t16;
          if ($[404] === Symbol.for("react.memo_cache_sentinel")) {
            t16 = <p className="text-caption text-foreground-secondary">Note must be saved before uploading documents</p>;
            $[404] = t16;
          } else {
            t16 = $[404];
          }
          let t17;
          if ($[405] !== t15) {
            t17 = <>{t15}{t16}</>;
            $[405] = t15;
            $[406] = t17;
          } else {
            t17 = $[406];
          }
          t14 = t17;
          break bb0;
        }
        let t15;
        if ($[407] !== question.required || $[408] !== questionData.name) {
          t15 = <Label className="text-body-strong" mandatory={question.required}>{questionData.name}</Label>;
          $[407] = question.required;
          $[408] = questionData.name;
          $[409] = t15;
        } else {
          t15 = $[409];
        }
        let t16;
        if ($[410] !== disabled || $[411] !== noteId || $[412] !== t15) {
          t16 = <NoteDocumentsSection noteId={noteId} disabled={disabled} label={t15} />;
          $[410] = disabled;
          $[411] = noteId;
          $[412] = t15;
          $[413] = t16;
        } else {
          t16 = $[413];
        }
        t14 = t16;
        break bb0;
      }
    case QuestionType.SIGNATURE:
      {
        const sigData = questionData as SignatureConfig;
        let t15;
        if ($[414] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[414] = questionData.name;
          $[415] = t15;
        } else {
          t15 = $[415];
        }
        let t16;
        if ($[416] !== sigData.text) {
          t16 = sigData.text && <p className="text-caption text-foreground-secondary">{sigData.text}</p>;
          $[416] = sigData.text;
          $[417] = t16;
        } else {
          t16 = $[417];
        }
        const t17 = typeof answer.data.value === "string" ? answer.data.value : null;
        let t18;
        if ($[418] !== handleChange) {
          t18 = value_0 => handleChange({
            value: value_0
          });
          $[418] = handleChange;
          $[419] = t18;
        } else {
          t18 = $[419];
        }
        let t19;
        if ($[420] !== disabled || $[421] !== t17 || $[422] !== t18) {
          t19 = <SignatureInput value={t17} onChange={t18} disabled={disabled} />;
          $[420] = disabled;
          $[421] = t17;
          $[422] = t18;
          $[423] = t19;
        } else {
          t19 = $[423];
        }
        let t20;
        if ($[424] !== t15 || $[425] !== t16 || $[426] !== t19) {
          t20 = <>{t15}{t16}{t19}</>;
          $[424] = t15;
          $[425] = t16;
          $[426] = t19;
          $[427] = t20;
        } else {
          t20 = $[427];
        }
        t14 = t20;
        break bb0;
      }
    case QuestionType.PROVIDER_SIGNATURE:
      {
        const psData = questionData as ProviderSignatureConfig;
        let t15;
        if ($[428] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[428] = questionData.name;
          $[429] = t15;
        } else {
          t15 = $[429];
        }
        let t16;
        if ($[430] !== psData.text) {
          t16 = psData.text && <p className="text-caption text-foreground-secondary">{psData.text}</p>;
          $[430] = psData.text;
          $[431] = t16;
        } else {
          t16 = $[431];
        }
        let t17;
        if ($[432] !== handleChange) {
          t17 = value => handleChange(value);
          $[432] = handleChange;
          $[433] = t17;
        } else {
          t17 = $[433];
        }
        const t18 = currentUser?.id;
        let t19;
        if ($[434] !== answer.data || $[435] !== disabled || $[436] !== t17 || $[437] !== t18) {
          t19 = <ProviderSignatureInput value={answer.data} onChange={t17} disabled={disabled} defaultProviderId={t18} />;
          $[434] = answer.data;
          $[435] = disabled;
          $[436] = t17;
          $[437] = t18;
          $[438] = t19;
        } else {
          t19 = $[438];
        }
        let t20;
        if ($[439] !== t15 || $[440] !== t16 || $[441] !== t19) {
          t20 = <>{t15}{t16}{t19}</>;
          $[439] = t15;
          $[440] = t16;
          $[441] = t19;
          $[442] = t20;
        } else {
          t20 = $[442];
        }
        t14 = t20;
        break bb0;
      }
    case QuestionType.DISCLAIMER:
      {
        const t15 = questionData as Disclaimer;
        let t16;
        if ($[443] !== t15) {
          t16 = <DisclaimerDisplay data={t15} />;
          $[443] = t15;
          $[444] = t16;
        } else {
          t16 = $[444];
        }
        t14 = t16;
        break bb0;
      }
    case QuestionType.STATEMENT:
      {
        const t15 = questionData as Statement;
        let t16;
        if ($[445] !== t15) {
          t16 = <StatementDisplay data={t15} />;
          $[445] = t15;
          $[446] = t16;
        } else {
          t16 = $[446];
        }
        t14 = t16;
        break bb0;
      }
    default:
      {
        let t15;
        if ($[447] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[447] = questionData.name;
          $[448] = t15;
        } else {
          t15 = $[448];
        }
        let t16;
        if ($[449] !== question.questionType) {
          t16 = <div className="text-body text-foreground-secondary">Unsupported question type: {question.questionType}</div>;
          $[449] = question.questionType;
          $[450] = t16;
        } else {
          t16 = $[450];
        }
        let t17;
        if ($[451] !== t15 || $[452] !== t16) {
          t17 = <>{t15}{t16}</>;
          $[451] = t15;
          $[452] = t16;
          $[453] = t17;
        } else {
          t17 = $[453];
        }
        t14 = t17;
      }
  }
  const fieldContent = t14;
  let t15;
  if ($[454] !== showPatientReportedPrefill) {
    t15 = showPatientReportedPrefill ? <div className="flex items-center"><MeasurementSourceBadge isUserReported={true} /></div> : null;
    $[454] = showPatientReportedPrefill;
    $[455] = t15;
  } else {
    t15 = $[455];
  }
  const patientReportedPrefillBadge = t15;
  if (question.questionType === QuestionType.TOGGLE) {
    let t16;
    if ($[456] !== fieldContent || $[457] !== patientReportedPrefillBadge) {
      t16 = patientReportedPrefillBadge ? <div className="space-y-1">{fieldContent}{patientReportedPrefillBadge}</div> : fieldContent;
      $[456] = fieldContent;
      $[457] = patientReportedPrefillBadge;
      $[458] = t16;
    } else {
      t16 = $[458];
    }
    return t16;
  }
  const isAttachmentsWithContext = question.questionType === QuestionType.SCRIBE_ATTACHMENTS && noteId && patientId;
  const isDocumentUploadWithNote = question.questionType === QuestionType.FILE_UPLOAD && noteId;
  const spacingClass = isAttachmentsWithContext || isDocumentUploadWithNote ? "space-y-4" : "";
  const t16 = fullHeight ? "flex flex-1 flex-col space-y-1" : "space-y-1";
  const t17 = spacingClass || undefined;
  let t18;
  if ($[459] !== t16 || $[460] !== t17) {
    t18 = cx(t16, t17);
    $[459] = t16;
    $[460] = t17;
    $[461] = t18;
  } else {
    t18 = $[461];
  }
  let t19;
  if ($[462] !== fieldContent || $[463] !== patientReportedPrefillBadge || $[464] !== t18) {
    t19 = <div className={t18}>{fieldContent}{patientReportedPrefillBadge}</div>;
    $[462] = fieldContent;
    $[463] = patientReportedPrefillBadge;
    $[464] = t18;
    $[465] = t19;
  } else {
    t19 = $[465];
  }
  return t19;
};

// Drawn-signature input backed by react-signature-canvas. Stores the signature
// as a data-URL string in answer.data.value. When disabled (read-only), shows
// the saved signature image instead of the canvas.
const SignatureInput: React.FC<{
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}> = t0 => {
  const $ = _c(18);
  const {
    value,
    onChange,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const sigCanvas = useRef(null);
  let t2;
  let t3;
  if ($[0] !== disabled || $[1] !== value) {
    t2 = () => {
      if (disabled) {
        return;
      }
      const canvas = sigCanvas.current;
      if (!canvas) {
        return;
      }
      if (value) {
        canvas.fromDataURL(value);
      } else {
        if (!canvas.isEmpty()) {
          canvas.clear();
        }
      }
    };
    t3 = [value, disabled];
    $[0] = disabled;
    $[1] = value;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== onChange) {
    t4 = () => {
      const canvas_0 = sigCanvas.current;
      if (!canvas_0) {
        return;
      }
      onChange(canvas_0.isEmpty() ? null : canvas_0.toDataURL());
    };
    $[4] = onChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const save = t4;
  let t5;
  if ($[6] !== onChange) {
    t5 = () => {
      sigCanvas.current?.clear();
      onChange(null);
    };
    $[6] = onChange;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const clear = t5;
  if (disabled) {
    let t6;
    if ($[8] !== value) {
      t6 = value ? <img src={value} alt="Signature" className="max-h-40 rounded border border-border bg-background" /> : <div className="text-body text-foreground-muted">Not signed</div>;
      $[8] = value;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      className: "w-full h-40"
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== save) {
    t7 = <div className="rounded border border-border bg-background p-2"><SignatureCanvas ref={sigCanvas} canvasProps={t6} clearOnResize={false} onEnd={save} /></div>;
    $[11] = save;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== clear) {
    t8 = <Button type="button" variant="secondary" size="sm" onClick={clear}>Clear</Button>;
    $[13] = clear;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="space-y-2">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};

// DISCLAIMER and STATEMENT are display-only — they render their configured
// content and never collect an answer.
const DisclaimerDisplay: React.FC<{
  data: Disclaimer;
}> = t0 => {
  const $ = _c(7);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.name) {
    t1 = data.name && <p className="mb-1 text-body-strong text-foreground">{data.name}</p>;
    $[0] = data.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== data.content) {
    t2 = data.content && <p className="whitespace-pre-wrap text-body text-foreground-secondary">{data.content}</p>;
    $[2] = data.content;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border border-border bg-background-secondary/40 p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const StatementDisplay: React.FC<{
  data: Statement;
}> = t0 => {
  const $ = _c(7);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.name) {
    t1 = data.name && <p className="mb-1 text-body-strong text-foreground">{data.name}</p>;
    $[0] = data.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== data.points) {
    t2 = data.points && data.points.length > 0 && <ul className="list-disc space-y-1 pl-5 text-body text-foreground-secondary">{data.points.map(_temp)}</ul>;
    $[2] = data.points;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border border-border bg-background-secondary/40 p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(point, index) {
  return <li key={index}>{point}</li>;
}